import React, {Fragment} from 'react'
import {Trans} from 'react-i18next'
import styled from 'styled-components'
import PageMessage from 'components/PageMessage'
import Bold from 'components/Bold'
import {SubtitleWithAboutLink} from 'apps/hosted-login/components/subtitles'
import LogisterForm from './logister-form'
import LogisterPageTitle from './LogisterPageTitle'
import {Label, LoginPageButton} from './LogisterPageStyles'
import {ELEMENTS_IDS, BIOMETRIC_TYPES} from 'const'
import LogisterFormDropdown from './webauthn/LogisterFormDropdown'
import {getBiometricHardwareForAppleDevice, isApple} from 'utils/deviceUtils'
import HintedLoginFormContainer from './hinted-login-form'
import SocialLogin from './social-login'
import Title from '../common/FormTitle'

const [FACE_ID, TOUCH_ID, ANY] = BIOMETRIC_TYPES
const NAME_BY_METHOD = {
  google: 'Google',
  facebook: 'Facebook',
  swissid: 'SwissID',
  apple: 'Apple',
}

const {biometricsLogin} = ELEMENTS_IDS.logisterPage

const WebAuthButtonsContainer = styled.div`
  margin-bottom: 40px;
`

const LoginErrorMessage = styled(PageMessage)`
  margin: 20px 0 20px 0;
  font-size: 16px;
`

const biometricType = getBiometricHardwareForAppleDevice()
const loginPageButtonLabel = isApple() ? `buttons.webauthn.apple.${biometricType}` : `buttons.webauthn.${biometricType}`

const joinProviders = (loginMethods, t) => {
  const and = t('hinted-login.and')
  return loginMethods.map(method => NAME_BY_METHOD[method]).join(` ${and} `)
}

const LogisterMethod = ({
  loginCase,
  loginWithSwissID,
  loginWithFacebook,
  loginWithGoogle,
  loginWithApple,
  checkIfUserExist,
  t,
  theme,
  withPlatformAuthentication,
  validCredentials,
  loginWithBiometrics,
  loginHint,
  loginMethods,
  isHintedLogin,
  showLoginHintCheckAnotherEmailMessage,
  getRegistrationLink,
  showNotification,
  gameConfig = {},
}) => {
  const biometricButtonThemes = {
    [FACE_ID]: theme.buttons.faceId,
    [TOUCH_ID]: theme.buttons.touchId,
    [ANY]: theme.buttons.anyId,
  }

  if (isHintedLogin) {
    const availableMethods = loginMethods.reduce((acc, method) => ({...acc, [method]: true}), {})
    const isNativeOnly = loginMethods.length === 1 && loginMethods[0] === 'native'
    const isFederatedOnly = !loginMethods.includes('native')
    return (
      <Fragment>
        <Title>{t('hinted-login.title')}</Title>
        <Label big>
          {!isFederatedOnly
            ? t('hinted-login.both')
            : t('hinted-login.third-party', {providers: joinProviders(loginMethods, t)})}
        </Label>
        {showLoginHintCheckAnotherEmailMessage && (
          <LoginErrorMessage warning>
            <Bold regular>{t('logister-login-hint-another-email-message-title')}</Bold>
            <br />
            <br />
            <Trans i18nKey="logister-login-hint-another-email-message-description">
              text
              <br />
              <br />
              text
              <Bold regular>{loginHint}</Bold>
              text
            </Trans>
          </LoginErrorMessage>
        )}
        {
          <HintedLoginFormContainer
            showNotification={showNotification}
            email={loginHint}
            isFederatedOnly={isFederatedOnly}
          />
        }
        {!isNativeOnly && (
          <SocialLogin
            showSeparator={!isNativeOnly && !isFederatedOnly}
            loginWithSwissID={loginWithSwissID}
            loginWithGoogle={loginWithGoogle}
            loginWithApple={loginWithApple}
            loginWithFacebook={loginWithFacebook}
            availableMethods={availableMethods}
          />
        )}
      </Fragment>
    )
  }
  return (
    <Fragment>
      {withPlatformAuthentication && (
        <WebAuthButtonsContainer>
          <LogisterPageTitle webauthn type={biometricType} />
          <SubtitleWithAboutLink />
          {Object.keys(validCredentials).map((key, idx) => (
            <LoginPageButton
              id={`${biometricsLogin}_${idx}`}
              label={loginPageButtonLabel}
              description={key}
              {...biometricButtonThemes[biometricType]}
              onClick={loginWithBiometrics(validCredentials[key])}
            />
          ))}
        </WebAuthButtonsContainer>
      )}
      <LogisterFormDropdown withPlatformAuthentication={withPlatformAuthentication}>
        <LogisterPageTitle loginCase={loginCase} gameTitle={gameConfig.title} />
        <LogisterForm t={t} theme={theme} onSubmit={checkIfUserExist} getRegistrationLink={getRegistrationLink} />
        {!withPlatformAuthentication && <SubtitleWithAboutLink />}
        <SocialLogin
          loginWithSwissID={loginWithSwissID}
          loginWithGoogle={loginWithGoogle}
          loginWithApple={loginWithApple}
          loginWithFacebook={loginWithFacebook}
          showSeparator
        />
      </LogisterFormDropdown>
    </Fragment>
  )
}

export default LogisterMethod
